import React from "react";

import { EmptyState, Text } from "@performyard/py-components";
import { EmployeeSummary } from "@performyard/service-contracts";

type BulletColumnProps = {
	title: string;
	items: ReadonlyArray<string>;
	testId: string;
};

const BulletColumn: React.FC<BulletColumnProps> = ({ title, items, testId }) => (
	<div className="flex min-w-0 flex-1 flex-col gap-2" data-testid={testId}>
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="h3">
			{title}
		</Text>
		<ul className="flex list-disc flex-col ps-5">
			{items.map((item) => (
				<li key={item}>
					<Text size="paragraph-3" color="neutral-9">
						{item}
					</Text>
				</li>
			))}
		</ul>
	</div>
);

export const SummarySection: React.FC<{ summary: EmployeeSummary }> = ({ summary }) => {
	const highlights = summary.top_highlights;
	const rightNow = summary.right_now;
	const hasRightNow = rightNow !== null && (rightNow.momentum.length > 0 || rightNow.watch_outs.length > 0);

	if (highlights === null && !hasRightNow) {
		return (
			<div className="mt-2" data-testid="summary-empty">
				<EmptyState variant="illustration" option={2} label="Not enough activity to summarize yet" />
			</div>
		);
	}

	return (
		<div className="mt-2 flex flex-col gap-4">
			{highlights !== null ? (
				<Text size="paragraph-3" color="neutral-9" data-testid="summary-headline">
					{highlights.summary}
				</Text>
			) : null}

			{highlights !== null &&
			(highlights.accomplishments.length > 0 || highlights.areas_of_improvement.length > 0) ? (
				<div className="flex gap-2">
					{highlights.accomplishments.length > 0 ? (
						<BulletColumn
							title="Accomplishments"
							items={highlights.accomplishments}
							testId="summary-accomplishments"
						/>
					) : null}

					{highlights.areas_of_improvement.length > 0 ? (
						<BulletColumn
							title="Areas of Improvement"
							items={highlights.areas_of_improvement}
							testId="summary-areas-of-improvement"
						/>
					) : null}
				</div>
			) : null}

			{rightNow !== null && (rightNow.momentum.length > 0 || rightNow.watch_outs.length > 0) ? (
				<div className="flex flex-col gap-2" data-testid="summary-right-now">
					<Text size="paragraph-2" weight="semi-bold" color="neutral-9" overrideElement="h3">
						Right Now (Last 30 Days)
					</Text>
					<div className="flex gap-2">
						{rightNow.momentum.length > 0 ? (
							<BulletColumn title="Momentum" items={rightNow.momentum} testId="summary-momentum" />
						) : null}
						{rightNow.watch_outs.length > 0 ? (
							<BulletColumn title="Watch Outs" items={rightNow.watch_outs} testId="summary-watch-outs" />
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
};
